import React, { ReactNode, useState } from "react";

import {
  Box,
  Text,
  HStack,
  VStack,
  WhooshTextProps,
} from "../../../../components/primitive";
import { WhooshThemeColors } from "../../../../theme/restyle/theme";

export type ScreenHeaderProps = {
  title?: string;
  subHeading?: ReactNode | string;
  leftItem?: ReactNode;
  rightItem?: ReactNode;
  backgroundColor?: WhooshThemeColors;
  variant?: "REGULAR" | "CONDENSED" | "CENTERED";
  titleRightElement?: ReactNode;
  isRounded?: boolean;
  scrolled?: boolean;
  includeHorizontalPadding?: boolean;
  bottomComponent?: ReactNode;
  hideTitleRightElementOnTitleWrap?: boolean;
  testID?: string;
};

export const ScreenHeader = ({
  title,
  subHeading,
  titleRightElement,
  leftItem,
  rightItem,
  backgroundColor = "surface-primary",
  variant = "REGULAR",
  isRounded = false,
  includeHorizontalPadding = true,
  bottomComponent,
  hideTitleRightElementOnTitleWrap = false,
  testID,
  scrolled,
}: ScreenHeaderProps) => {
  const isCondensed = variant === "CONDENSED";
  const isCentered = variant === "CENTERED";
  const [titleLineCount, setTitleLineCount] = useState(1);

  const shadowProps = {
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 2,
    shadowColor: "black" as WhooshThemeColors,
  };
  const shouldShowShadow = (isCondensed || isCentered) && isRounded && scrolled;
  const validShadowProps = shouldShowShadow ? shadowProps : {};
  const shouldHideTitleRightElement =
    hideTitleRightElementOnTitleWrap && titleLineCount > 1;

  const handleTitleTextLayout: WhooshTextProps["onTextLayout"] = (event) => {
    if (!hideTitleRightElementOnTitleWrap) {
      return;
    }

    setTitleLineCount(event.nativeEvent.lines.length);
  };

  return (
    <VStack
      backgroundColor={backgroundColor}
      paddingTop={6}
      px={includeHorizontalPadding ? 6 : 0}
      pb={isCondensed || isCentered ? 4 : 2}
      borderBottomLeftRadius={isRounded ? "6" : "0"}
      borderBottomRightRadius={isRounded ? "6" : "0"}
      gap={isCondensed || isCentered ? 4 : 6}
      testID={testID}
      {...validShadowProps}
    >
      {leftItem || rightItem ? (
        <HStack
          justifyContent="space-between"
          minHeight={56}
          alignItems="center"
        >
          {leftItem ? leftItem : <Box width={56} />}
          {isCentered ? (
            <VStack alignItems="center">
              {subHeading ? (
                <Text variant="mainTiny" color="text-secondary">
                  {subHeading}
                </Text>
              ) : null}
              {title ? <Text variant="mainBaseSemiBold">{title}</Text> : null}
            </VStack>
          ) : null}
          {rightItem ? rightItem : <Box width={56} />}
        </HStack>
      ) : null}

      {!isCentered && (title || subHeading || titleRightElement) ? (
        <HStack justifyContent="space-between" alignItems="center">
          <VStack gap={isCondensed ? 0 : 1} flexWrap="wrap" flex={1}>
            {subHeading ? (
              <Text
                variant={isCondensed ? "mainTiny" : "mainSmallSemiBold"}
                color="text-secondary"
              >
                {subHeading}
              </Text>
            ) : null}
            {title ? (
              <Box width="100%">
                <Text
                  variant={isCondensed ? "mainHeading" : "mainTitle2"}
                  mt={subHeading ? 1 : 0}
                  onTextLayout={handleTitleTextLayout}
                >
                  {title}
                </Text>
              </Box>
            ) : null}
          </VStack>
          {titleRightElement && !shouldHideTitleRightElement ? (
            <Box>{titleRightElement}</Box>
          ) : null}
        </HStack>
      ) : null}
      {bottomComponent ? bottomComponent : null}
    </VStack>
  );
};
